/**
 * RealisticPlate - Accurate German License Plate
 *
 * Uses real images for EU stars, TÜV sticker, and state seal.
 * Matches the style from wunsch.kennzeichenpro24.de exactly:
 * - Double rounded border (outer gray + inner white gap)
 * - Each input zone in its own light gray rounded box
 * - Seals stacked vertically (TÜV on top, state seal below)
 * - Supports all plate sizes and vehicle types
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  type PlateSize,
  type PlateInput,
  type PlateType,
  type VehicleCategory,
  type PlateRule,
  validateLettersValue,
  validateNumbersValue,
  getMaxLettersForCityCode,
  getMaxNumbersForCombo,
  isForbiddenLetterCombination,
} from '@/services/rules/plateRulesEngine'
import { filterCityCodes, isValidCityCode } from '@/data/germanCityCodes'

// ============================================================================
// TYPES
// ============================================================================

interface RealisticPlateProps {
  plateSize: PlateSize
  plateType: PlateType
  vehicleCategory: VehicleCategory
  input: PlateInput
  interactive?: boolean
  rule?: PlateRule
  onInputChange?: (input: PlateInput) => void
  seasonFrom?: number  // 1-12
  seasonTo?: number    // 1-12
  className?: string
}

type ActiveZone = 'cityCode' | 'letters' | 'numbers' | null

// ============================================================================
// EU BAND - Blue strip with real star image + "D"
// ============================================================================

const EUBand = ({ width, height }: { width: number; height: number }) => {
  const dSize = Math.max(width * 0.55, 12)
  const starsPad = Math.max(width * 0.08, 2)

  return (
    <div
      className="flex-shrink-0 flex flex-col items-center justify-between"
      style={{
        width: `${width}px`,
        height: '100%',
        background: 'linear-gradient(180deg, #003399 0%, #002266 100%)',
        borderRadius: '5px 0 0 5px',
        padding: `${height * 0.08}px ${starsPad}px ${height * 0.1}px`,
      }}
    >
      <img
        src="/assets/Stars.svg"
        alt="EU"
        style={{
          width: `${width * 0.78}px`,
          height: `${width * 0.78}px`,
          objectFit: 'contain',
        }}
      />
      <span
        className="text-white font-black leading-none"
        style={{ fontSize: `${dSize}px`, marginBottom: `${height * 0.02}px` }}
      >
        D
      </span>
    </div>
  )
}

// ============================================================================
// SEALS - Real images stacked vertically (TÜV on top, state seal below)
// ============================================================================

const Seals = ({ size }: { size: number }) => (
  <div className="flex flex-col items-center flex-shrink-0" style={{ gap: `${size * 0.12}px` }}>
    <img
      src="/assets/01.svg"
      alt="TÜV"
      className="rounded-full"
      style={{ width: `${size}px`, height: `${size}px`, objectFit: 'cover' }}
    />
    <img
      src="/assets/02.webp"
      alt="Seal"
      className="rounded-full"
      style={{ width: `${size * 1.05}px`, height: `${size * 1.05}px`, objectFit: 'cover' }}
    />
  </div>
)

// ============================================================================
// ZONE BOX - Light gray rounded rectangle for each input section
// ============================================================================

const ZoneBox = ({
  children,
  active,
  arrowSize,
  style,
  animated = true,
}: {
  children: React.ReactNode
  active?: boolean
  arrowSize?: number
  style?: React.CSSProperties
  animated?: boolean
}) => {
  const baseStyle: React.CSSProperties = {
    position: 'relative',
    background: active
      ? 'linear-gradient(180deg, #eef6ff 0%, #e4effc 100%)'
      : 'linear-gradient(180deg, #ececec 0%, #ddd 100%)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    transition: 'background 0.3s, outline-color 0.3s, box-shadow 0.3s',
    animation: active ? 'zoneGlowPulse 1.8s ease-in-out infinite' : 'none',
    outline: active ? '2px solid rgba(33, 150, 243, 0.7)' : '2px solid transparent',
    outlineOffset: '1px',
    border: '1px solid rgba(0,0,0,0.08)',
    ...style,
  }

  // Static mode: plain divs (avoids Framer Motion DOM crash with conditional children)
  if (!animated) {
    return (
      <div style={baseStyle}>
        <div className="flex items-center justify-center w-full">{children}</div>
      </div>
    )
  }

  return (
    <motion.div
      layout
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      style={baseStyle}
    >
      {active && arrowSize && (
        <div
          style={{
            position: 'absolute',
            top: `${-(arrowSize * 0.8 + 20)}px`,
            left: '50%',
            animation: 'arrowBounce 1.8s ease-in-out infinite',
            filter: 'drop-shadow(0 0 8px rgba(33, 150, 243, 0.7))',
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <svg
            width={arrowSize * 1.4}
            height={arrowSize * 0.9}
            viewBox="0 0 24 16"
            fill="none"
          >
            <path d="M3 3L12 13L21 3" stroke="#2196f3" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
      <motion.div layout="position" className="flex items-center justify-center w-full">
        {children}
      </motion.div>
    </motion.div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RealisticPlate = ({
  plateSize,
  plateType,
  vehicleCategory: _vc,
  input,
  interactive = false,
  rule,
  onInputChange,
  seasonFrom = 4,
  seasonTo = 10,
  className = '',
}: RealisticPlateProps) => {
  void _vc

  const [activeZone, setActiveZone] = useState<ActiveZone>(interactive ? 'cityCode' : null)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSugg, setShowSugg] = useState(false)
  const [valMsg, setValMsg] = useState<string | null>(null)

  const ccRef = useRef<HTMLInputElement>(null!)
  const ltRef = useRef<HTMLInputElement>(null!)
  const nmRef = useRef<HTMLInputElement>(null!)

  // ── Layout ───────────────────────────────────────────────────────────────
  const ar = plateSize.width / plateSize.height
  const isOneLine = ar > 2.5
  const isTall = ar < 0.7
  const isSquare = !isTall && ar <= 1.3

  // Plate outer dimensions
  let W: number
  if (isTall) W = 180
  else if (isSquare) W = 260
  else if (!isOneLine) W = 420
  else W = 580

  const H = W / ar
  const pad = isOneLine ? 6 : 5
  const innerBorder = 3 // inner border frame thickness
  const contentH = H - pad * 2 - innerBorder * 2
  const innerH = contentH
  const euW = isTall ? (W - pad * 2) * 0.2 : isSquare ? (W - pad * 2) * 0.13 : (W - pad * 2) * 0.085

  // Font - scaled larger for Sauerkrauto font to be more prominent
  let fs: number
  if (isOneLine) fs = innerH * 0.75
  else if (isTall) fs = (W - pad * 2) * 0.35
  else if (isSquare) fs = innerH * 0.28
  else fs = innerH * 0.32
  fs = Math.min(Math.max(fs, 16), 80)

  const sealSz = isOneLine ? Math.max(innerH * 0.3, 13) : Math.max(fs * 0.40, 11)
  // Sauerkrauto is relatively narrow, char width is approximately 0.52 of font size
  const charW = fs * 0.52
  const gap = isOneLine ? Math.max(fs * 0.12, 4) : Math.max(fs * 0.10, 3)

  // Zone box height - smaller to leave breathing room
  const zoneH = isOneLine ? innerH * 0.7 : innerH * 0.4

  // ── Hooks ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (interactive) setTimeout(() => { ccRef.current?.focus(); setActiveZone('cityCode') }, 100)
  }, [interactive])

  // Auto-advance between zones
  useEffect(() => {
    if (!interactive || !rule) return
    const cc = input.cityCode || ''

    // Auto-advance from cityCode → letters when valid city code at max length
    if (activeZone === 'cityCode' && cc.length === rule.maxCityCodeLength && isValidCityCode(cc) && !input.letters) {
      setTimeout(() => { setActiveZone('letters'); ltRef.current?.focus() }, 200)
    }

    // Auto-advance from letters → numbers when at max letters
    if (activeZone === 'letters') {
      const lt = input.letters || ''
      const maxL = getMaxLettersForCityCode(rule, cc.length)
      if (lt.length >= maxL && lt.length > 0 && !input.numbers) {
        setTimeout(() => { setActiveZone('numbers'); nmRef.current?.focus() }, 200)
      }
    }
  }, [input, activeZone, rule, interactive])

  // City code autocomplete suggestions
  useEffect(() => {
    if (!interactive) return
    const cc = input.cityCode || ''
    if (cc.length > 0 && !cc.includes('?')) {
      const s = filterCityCodes(cc).slice(0, 8)
      setSuggestions(s)
      setShowSugg(s.length > 0 && activeZone === 'cityCode')
    } else { setSuggestions([]); setShowSugg(false) }
  }, [input.cityCode, interactive, activeZone])

  // ── Handlers ─────────────────────────────────────────────────────────────

  // City Code: only A-Z + ÄÖÜ (umlauts exist in city codes like FÜS, BÖD)
  // Must match a valid city code from the database
  const handleCC = useCallback((v: string) => {
    if (!rule || !onInputChange) return
    const u = v.toUpperCase().replace(/[^A-ZÄÖÜ?]/g, '')
    if (!u.includes('?') && u.length > rule.maxCityCodeLength) return

    const ni = { ...input, cityCode: u }

    // Wildcard mode - skip validation
    if (u.includes('?')) { onInputChange(ni); setValMsg(null); return }

    // Check total length
    const totalLen = u.length + (input.letters?.length || 0) + (input.numbers?.length || 0)
    if (totalLen > rule.maxTotalChars) {
      setValMsg(`Gesamtlänge darf ${rule.maxTotalChars} Zeichen nicht überschreiten`)
      return
    }

    onInputChange(ni)

    // Warn if typed city code doesn't exist in database
    if (u.length >= rule.minCityCodeLength && !isValidCityCode(u)) {
      setValMsg(`"${u}" ist kein gültiger Stadtcode`)
    } else {
      setValMsg(null)
    }
  }, [rule, input, onInputChange])

  // Letters: only A-Z (NO umlauts - German plates only allow A-Z for custom letters)
  // Checks forbidden combinations (SS, SA, HJ, NS, KZ, etc.)
  const handleLT = useCallback((v: string) => {
    if (!rule || !onInputChange) return
    const u = v.toUpperCase().replace(/[^A-Z?]/g, '')

    // Wildcard mode
    if (u.includes('?')) {
      if (u.length > 2) return
      onInputChange({ ...input, letters: u }); setValMsg(null); return
    }

    // Require valid city code first
    const cc = input.cityCode || ''
    if (cc.length < rule.minCityCodeLength) {
      setValMsg('Bitte zuerst Stadtcode eingeben'); return
    }
    if (!cc.includes('?') && !isValidCityCode(cc)) {
      setValMsg('Bitte gültigen Stadtcode auswählen'); ccRef.current?.focus(); return
    }

    // Check max letters for this city code length
    const maxLetters = getMaxLettersForCityCode(rule, cc.length)
    if (u.length > maxLetters) return

    // Check forbidden combinations
    if (u.length >= 2) {
      const forbidden = isForbiddenLetterCombination(u)
      if (forbidden) { setValMsg(forbidden); return }
    }

    // Validate using new direct validation (no off-by-one)
    const ni = { ...input, letters: u }
    const r = validateLettersValue(rule, ni)
    if (r.valid) { onInputChange(ni); setValMsg(null) }
    else { setValMsg(r.error || 'Ungültige Kombination') }
  }, [rule, input, onInputChange])

  // Numbers: only 0-9, max determined by rule + city code + letters combo
  const handleNM = useCallback((v: string) => {
    if (!rule || !onInputChange) return
    const n = v.replace(/[^0-9?]/g, '')

    // Wildcard mode
    if (n.includes('?')) {
      if (n.length > 4) return
      onInputChange({ ...input, numbers: n }); setValMsg(null); return
    }

    // Require valid city code
    const cc = input.cityCode || ''
    if (cc.length < rule.minCityCodeLength) {
      setValMsg('Bitte zuerst Stadtcode eingeben'); return
    }

    // Require letters
    const lt = input.letters || ''
    if (lt.length === 0) {
      setValMsg('Bitte zuerst Buchstaben eingeben'); return
    }

    // Check max numbers for this city code + letters combo
    const maxNumbers = getMaxNumbersForCombo(rule, cc.length, lt.length)
    if (n.length > maxNumbers) return

    // Validate using new direct validation (no off-by-one)
    const ni = { ...input, numbers: n }
    const r = validateNumbersValue(rule, ni)
    if (r.valid) { onInputChange(ni); setValMsg(null) }
    else { setValMsg(r.error || 'Ungültige Kombination') }
  }, [rule, input, onInputChange])

  // City code suggestion selected from dropdown
  const handleSugg = useCallback((s: string) => {
    if (!onInputChange) return
    onInputChange({ ...input, cityCode: s })
    setShowSugg(false); setActiveZone('letters'); setValMsg(null)
    setTimeout(() => ltRef.current?.focus(), 100)
  }, [input, onInputChange])

  // ── Values ───────────────────────────────────────────────────────────────
  const cc = input.cityCode || ''
  const lt = input.letters || ''
  const nm = input.numbers || ''
  const typeInd = (plateType === 'E' || plateType === 'E_SEASON') ? 'E' : (plateType === 'H' || plateType === 'H_SEASON') ? 'H' : null
  const isSeason = plateType.includes('SEASON')

  // ── Font style ───────────────────────────────────────────────────────────
  const pf: React.CSSProperties = {
    fontFamily: "'Sauerkrauto', 'Arial Black', 'Segoe UI Black', Impact, Arial, sans-serif",
    fontWeight: 'normal',
    fontSize: `${fs}px`,
    letterSpacing: `${fs * 0.02}px`,
    lineHeight: 1,
    color: '#333',
  }

  // ── Extra elements present (E/H indicator or season) ────────────────────
  const hasExtras = !!(typeInd || isSeason)

  // ── Render zone input/text ───────────────────────────────────────────────
  const renderZone = (
    ref: React.RefObject<HTMLInputElement>,
    value: string,
    zone: ActiveZone,
    onChange: (v: string) => void,
    maxLen: number,
    placeholder: string,
    disabled?: boolean,
    minWidth?: number,
  ) => {
    const isActive = activeZone === zone
    const contentChars = value.length || placeholder.length
    const minChars = minWidth || 1.5
    const baseW = Math.max(contentChars, minChars) * charW
    // When extra elements (E/H, season) are present, zones can flex-shrink to fit
    const zoneStyle: React.CSSProperties = hasExtras
      ? { padding: `0 ${fs * 0.1}px`, height: `${zoneH}px`, minWidth: `${Math.max(minChars * charW * 0.65, charW)}px`, width: `${baseW + fs * 0.2}px`, flexShrink: 1 }
      : { padding: `0 ${fs * 0.15}px`, height: `${zoneH}px`, width: `${baseW + fs * 0.3}px` }

    if (!interactive) {
      return (
        <ZoneBox style={zoneStyle} animated={false}>
          <span style={pf}>{value || <span style={{ opacity: 0.35 }}>{placeholder}</span>}</span>
        </ZoneBox>
      )
    }

    return (
      <ZoneBox
        active={isActive}
        arrowSize={isActive ? Math.max(fs * 0.25, 12) : undefined}
        style={zoneStyle}
        animated={true}
      >
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            if (zone === 'letters' && cc.length < (rule?.minCityCodeLength || 1)) { ccRef.current?.focus(); return }
            if (zone === 'numbers') {
              if (cc.length < (rule?.minCityCodeLength || 1)) { ccRef.current?.focus(); return }
              if (lt.length === 0) { ltRef.current?.focus(); return }
            }
            setActiveZone(zone)
          }}
          disabled={disabled}
          className="bg-transparent border-none outline-none text-center w-full disabled:opacity-25"
          style={{ ...pf, caretColor: '#333', height: `${fs * 1.1}px`, padding: 0 }}
          maxLength={maxLen}
          autoComplete="off"
          placeholder={placeholder}
        />
      </ZoneBox>
    )
  }

  const renderTypeInd = () => typeInd
    ? <ZoneBox style={{ padding: `0 ${fs * 0.1}px`, height: `${zoneH}px` }} animated={interactive}>
      <span style={{ ...pf, color: '#1565c0' }}>{typeInd}</span>
    </ZoneBox>
    : null

  const renderSeason = () => {
    if (!isSeason) return null
    const sz = Math.max(fs * 0.26, 8)
    const fromStr = String(seasonFrom).padStart(2, '0')
    const toStr = String(seasonTo).padStart(2, '0')
    const Wrap = interactive ? motion.div : 'div'
    const wrapProps = interactive ? { layout: true } : {}
    return (
      <Wrap {...wrapProps} className="flex flex-col items-center flex-shrink-0" style={{ lineHeight: 1, marginLeft: `${gap}px` }}>
        <span className="font-black" style={{ fontSize: `${sz}px`, color: '#333' }}>{fromStr}</span>
        <div style={{ width: `${sz * 1.3}px`, height: '1px', background: '#333', margin: '1px 0' }} />
        <span className="font-black" style={{ fontSize: `${sz}px`, color: '#333' }}>{toStr}</span>
      </Wrap>
    )
  }

  const layoutOneLine = () => {
    if (!interactive) {
      return (
        <div
          className="flex items-center justify-center h-full flex-1"
          style={{ gap: `${gap}px`, padding: `${gap * 0.5}px ${gap * 1.2}px` }}
        >
          {renderZone(ccRef, cc, 'cityCode', handleCC, rule?.maxCityCodeLength || 3, 'B', false, 2)}
          <div className="flex-shrink-0"><Seals size={sealSz} /></div>
          {renderZone(ltRef, lt, 'letters', handleLT, 2, 'AB', true, 2)}
          {renderZone(nmRef, nm, 'numbers', handleNM, 4, '1234', true, 4)}
          {renderTypeInd()}
          {renderSeason()}
        </div>
      )
    }
    return (
      <motion.div
        layout
        className="flex items-center justify-center h-full flex-1"
        style={{ gap: `${gap}px`, padding: `${gap * 0.5}px ${gap * 1.2}px` }}
      >
        {renderZone(ccRef, cc, 'cityCode', handleCC, rule?.maxCityCodeLength || 3, 'B', false, 2)}
        <motion.div layout className="flex-shrink-0"><Seals size={sealSz} /></motion.div>
        {renderZone(ltRef, lt, 'letters', handleLT, 2, 'AB', cc.length < (rule?.minCityCodeLength || 1), 2)}
        {renderZone(nmRef, nm, 'numbers', handleNM, 4, '1234', cc.length < (rule?.minCityCodeLength || 1) || lt.length === 0, 4)}
        {renderTypeInd()}
        {renderSeason()}
      </motion.div>
    )
  }

  const layoutTwoLine = () => {
    if (!interactive) {
      return (
        <div className="flex flex-col justify-center h-full flex-1" style={{ gap: `${gap}px`, padding: `${gap * 1.2}px ${gap}px` }}>
          <div className="flex items-center justify-center" style={{ gap: `${gap * 1.5}px`, height: '45%' }}>
            {renderZone(ccRef, cc, 'cityCode', handleCC, rule?.maxCityCodeLength || 3, 'B', false, 2)}
            <Seals size={sealSz} />
          </div>
          <div className="flex items-center justify-center" style={{ gap: `${gap}px`, height: '45%' }}>
            {renderZone(ltRef, lt, 'letters', handleLT, 2, 'AB', true, 2)}
            {renderZone(nmRef, nm, 'numbers', handleNM, 4, '1234', true, 4)}
            {renderTypeInd()}
            {renderSeason()}
          </div>
        </div>
      )
    }
    return (
      <motion.div layout className="flex flex-col justify-center h-full flex-1" style={{ gap: `${gap}px`, padding: `${gap * 1.2}px ${gap}px` }}>
        <motion.div layout className="flex items-center justify-center" style={{ gap: `${gap * 1.5}px`, height: '45%' }}>
          {renderZone(ccRef, cc, 'cityCode', handleCC, rule?.maxCityCodeLength || 3, 'B', false, 2)}
          <Seals size={sealSz} />
        </motion.div>
        <motion.div layout className="flex items-center justify-center" style={{ gap: `${gap}px`, height: '45%' }}>
          {renderZone(ltRef, lt, 'letters', handleLT, 2, 'AB', cc.length < (rule?.minCityCodeLength || 1), 2)}
          {renderZone(nmRef, nm, 'numbers', handleNM, 4, '1234', cc.length < (rule?.minCityCodeLength || 1) || lt.length === 0, 4)}
          {renderTypeInd()}
          {renderSeason()}
        </motion.div>
      </motion.div>
    )
  }

  const layoutVertical = () => {
    if (!interactive) {
      return (
        <div className="flex flex-col items-center justify-center h-full flex-1" style={{ gap: `${gap}px`, padding: `${gap}px` }}>
          {renderZone(ccRef, cc, 'cityCode', handleCC, rule?.maxCityCodeLength || 3, 'B', false, 2)}
          <Seals size={sealSz} />
          <div className="flex items-center" style={{ gap: `${gap}px` }}>
            {renderZone(ltRef, lt, 'letters', handleLT, 2, 'AB', true, 1.5)}
            {renderZone(nmRef, nm, 'numbers', handleNM, 4, '1234', true, 4)}
          </div>
          {renderTypeInd()}
          {renderSeason()}
        </div>
      )
    }
    return (
      <motion.div layout className="flex flex-col items-center justify-center h-full flex-1" style={{ gap: `${gap}px`, padding: `${gap}px` }}>
        {renderZone(ccRef, cc, 'cityCode', handleCC, rule?.maxCityCodeLength || 3, 'B', false, 2)}
        <Seals size={sealSz} />
        <motion.div layout className="flex items-center" style={{ gap: `${gap}px` }}>
          {renderZone(ltRef, lt, 'letters', handleLT, 2, 'AB', cc.length < (rule?.minCityCodeLength || 1), 1.5)}
          {renderZone(nmRef, nm, 'numbers', handleNM, 4, '1234', cc.length < (rule?.minCityCodeLength || 1) || lt.length === 0, 4)}
        </motion.div>
        {renderTypeInd()}
        {renderSeason()}
      </motion.div>
    )
  }

  // ── Arrow size for active zone ──────────────────────────────────────────
  const arrowH = Math.max(fs * 0.38, 14)
  const arrowSpace = interactive ? arrowH + 35 : 5

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className={`relative ${className}`} style={{ paddingTop: `${arrowSpace}px` }}>
      {/* Keyframe animations for active zone glow + arrow */}
      <style>{`
        @keyframes zoneGlowPulse {
          0%, 100% { box-shadow: 0 0 6px 2px rgba(33, 150, 243, 0.15), inset 0 0 2px rgba(33, 150, 243, 0.04); }
          50% { box-shadow: 0 0 22px 8px rgba(33, 150, 243, 0.55), inset 0 0 8px rgba(33, 150, 243, 0.15); }
        }
        @keyframes arrowBounce {
          0%, 100% { transform: translateX(-50%) translateY(0); opacity: 0.7; }
          50% { transform: translateX(-50%) translateY(6px); opacity: 1; }
        }
      `}</style>
      {/* Autocomplete */}
      {interactive && showSugg && suggestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full z-50 bg-dark-800 border border-gray-700 rounded-lg shadow-2xl max-h-48 overflow-y-auto"
          style={{ minWidth: '140px' }}
        >
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => handleSugg(s)}
              className="w-full px-4 py-2 text-left text-white hover:bg-primary-500/20 transition-colors text-sm font-semibold"
            >
              {s}
            </button>
          ))}
        </motion.div>
      )}

      {/* Plate - realistic German license plate */}
      <div
        className="mx-auto"
        style={{
          width: `${W}px`,
          height: `${H}px`,
          maxWidth: '100%',
          border: '3px solid #2a2a2a',
          borderRadius: '8px',
          padding: `${pad}px`,
          background: 'linear-gradient(175deg, #fefefe 0%, #f5f2ec 50%, #f0ede6 100%)',
          boxShadow: '0 6px 28px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.6)',
          overflow: 'visible',
        }}
      >
        {/* Inner border frame - characteristic of real German plates */}
        <div
          style={{
            width: '100%',
            height: '100%',
            border: '3px solid #888',
            borderRadius: '5px',
            overflow: 'visible',
          }}
        >
          {/* Plate content */}
          <div
            className="flex"
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '4px',
              background: 'linear-gradient(175deg, #fefefe 0%, #f8f6f0 40%, #f5f2ec 100%)',
            }}
          >
            <EUBand width={euW} height={innerH} />
            {isOneLine ? layoutOneLine() : isTall ? layoutVertical() : layoutTwoLine()}
          </div>
        </div>
      </div>

      {/* Validation */}
      {interactive && valMsg && (
        <div className="mt-2 mx-auto p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs text-center" style={{ maxWidth: `${W}px` }}>
          {valMsg}
        </div>
      )}

      {/* Size */}
      <div className="text-center text-xs text-gray-500" style={{ maxWidth: `${W}px`, margin: '4px auto 0' }}>
        {plateSize.width} x {plateSize.height} mm
        {plateSize.layout === 'zweizeilig' ? ' (Zweizeilig)' : ' (Einzeilig)'}
      </div>
    </div>
  )
}

export default RealisticPlate
